import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import ItineraryTimeline from "../components/ItineraryTimeline";
import { eur, STATUS_LABEL } from "../lib/format";
import { MapPin, Loader2, Check, Pencil } from "lucide-react";

const STEP_LABELS = ["Itinerary", "Requested", "Availability Confirmed", "Waiting for Payment", "Confirmed"];
const STEP_RANK = { itinerary_sent: 0, requested: 1, availability_confirmed: 2, waiting_for_payment: 3, confirmed: 4, enriched: 4, completed: 4 };
const STATUS_NOTICE = {
  requested: "Thank you — your booking request has been received. We're confirming availability with our partners and will update you shortly.",
  availability_confirmed: "Great news — availability is confirmed for your dates! We'll email your invoice with bank transfer details shortly.",
  waiting_for_payment: "Your invoice has been emailed with bank transfer details. Please complete your reservation payment to confirm your journey.",
};

export default function TripDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [requesting, setRequesting] = useState(false);

  const load = () => api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch(() => toast.error("Booking not found"));
  useEffect(() => { load(); }, [id]);

  const requestInvoice = async () => {
    setRequesting(true);
    try {
      await api.post(`/bookings/${id}/request-invoice`);
      toast.success("Booking request sent");
      await load();
    } catch {
      toast.error("Could not send your request — please try again");
      setRequesting(false);
    }
  };

  if (!b) return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-7 h-7 text-rr-gold animate-spin" strokeWidth={1} /></div>;
  const requested = b.status !== "itinerary_sent";
  const currentStep = STEP_RANK[b.status] ?? 0;

  return (
    <div data-testid="trip-detail-page">
      <section className="relative h-[54vh] min-h-[400px] overflow-hidden -mt-[74px] grain">
        <img src={b.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 h-full max-w-5xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-12">
          <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-rr-gold" strokeWidth={1.4} /><span className="overline">{b.area}</span></div>
          <h1 className="font-heading text-6xl md:text-7xl mt-3 text-rr-ivory">{b.package_name}</h1>
          <div className="flex items-center gap-3 mt-4">
            <span className="inline-flex w-fit text-xs px-3 py-1.5 rounded-full bg-rr-gold/15 text-rr-gold" data-testid="trip-status">{STATUS_LABEL[b.status] || b.status}</span>
            {!requested && (
              <Link to={`/trips?edit=${b.booking_id}`} data-testid="edit-journey-btn" className="inline-flex items-center gap-1.5 text-xs text-rr-ivory/80 hover:text-rr-gold uppercase tracking-wide">
                <Pencil className="w-3.5 h-3.5" /> Edit journey
              </Link>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-14 grid lg:grid-cols-[1fr_360px] gap-12">
        <div>
          <div className="flex items-center justify-between mb-14" data-testid="trip-progress">
            {STEP_LABELS.map((label, i) => (
              <div key={label} className="flex-1 flex flex-col items-center relative">
                {i > 0 && <div className={`absolute right-1/2 top-3 h-px w-full ${i <= currentStep ? "bg-rr-gold" : "bg-rr-line"}`} />}
                <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${i <= currentStep ? "bg-rr-gold text-rr-bg" : "bg-rr-surface border border-rr-line text-rr-muted"}`}>
                  {i <= currentStep ? <Check className="w-3 h-3" strokeWidth={2} /> : i + 1}
                </div>
                <span className={`text-[10px] mt-2 ${i <= currentStep ? "text-rr-ivory" : "text-rr-muted"}`}>{label}</span>
              </div>
            ))}
          </div>

          {b.concierge_note && (
            <div className="rounded-xl bg-rr-ocean/15 border border-rr-ocean/30 p-6 mb-10">
              <span className="overline text-rr-ocean">A note from your concierge</span>
              <p className="text-rr-ivory mt-2 leading-relaxed">{b.concierge_note}</p>
            </div>
          )}
          <ItineraryTimeline itinerary={b.itinerary} />
        </div>

        <div className="lg:sticky lg:top-[98px] self-start">
          <div className="rounded-xl border border-rr-line bg-rr-surface p-6">
            <span className="overline text-rr-muted">Reservation</span>
            <div className="mt-4 space-y-2 text-sm">
              <Row label="Nights" value={b.nights} />
              <Row label="Guests" value={b.guests} />
              {b.start_date && <Row label="Arrival" value={b.start_date} />}
              <div className="border-t border-rr-line my-3" />
              <Row label="Total" value={eur(b.pricing.total)} bold />
            </div>
            {!requested ? (
              <div className="mt-6 space-y-3">
                <button onClick={requestInvoice} disabled={requesting} data-testid="request-invoice-btn" className="btn-gold w-full py-3.5 flex items-center justify-center gap-2 text-xs uppercase">
                  {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Booking Request"}
                </button>
                <p className="text-[11px] text-rr-muted text-center leading-relaxed">
                  No payment is taken online. Our team will confirm availability, then email your invoice with bank transfer details.
                </p>
              </div>
            ) : (
              <div className="mt-6 rounded-lg bg-rr-forest/40 border border-rr-gold/20 p-5 text-center" data-testid="invoice-requested-notice">
                <Check className="w-6 h-6 text-rr-gold mx-auto" strokeWidth={1.4} />
                <p className="text-sm text-rr-ivory mt-2 leading-relaxed">
                  {STATUS_NOTICE[b.status] || "Your booking is progressing — we'll keep you updated by email at every step."}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-rr-muted">{label}</span>
      <span className={bold ? "text-rr-ivory font-semibold" : "text-rr-ivory"}>{value}</span>
    </div>
  );
}
